import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

/**
 * Driver roster per zone (jsonb `zones.drivers`) — master data for the
 * Master · Zone page. Drivers have no local master here: every row is a
 * `{id, code, name}` snapshot taken from the driver-service at save time,
 * exactly like `dispatches.ridersSnapshot`. There is no server-side lookup,
 * so the FE always sends whole driver objects.
 *
 * There is no hub-wide roster endpoint: the roster rides along on
 * `GET /v1/hubs/:hubId/zones` (see `zonesAPI.listByHub` — `Zone.drivers`),
 * so the page load already has every zone's roster. This client only covers
 * the single-zone operations the drawer needs.
 *
 * Naming: data-layer names follow the driver-service (`driver` everywhere —
 * paths, JSON fields, types, DB). The **UI says "mitra"** because that's what
 * ops calls them; that translation lives entirely in the components, never
 * here. `driverId` is the same numeric identity as `riders[].id` in dispatch
 * preview.
 *
 * Fase 1 is reference data only — `POST /v1/dispatch/preview` still takes its
 * `riders[]` manually and is untouched by anything here.
 */

/**
 * Client a driver is scoped to — core-service snapshot, the same numeric
 * identity as dispatch preview `clientIds`. An empty `clients` array means
 * the driver serves every client in the zone.
 */
export interface ZoneDriverClient {
  id: number;
  name: string;
}

/** A roster row as the API returns it. `priority` is always dense 1..N. */
export interface ZoneDriver {
  driverId: number;
  driverCode: string;
  driverName: string;
  clients: ZoneDriverClient[];
  priority: number;
}

/** A roster row as the API accepts it — priority is the array index. */
export interface ZoneDriverInput {
  id: number;
  code: string;
  name: string;
  /** [] = serves every client. */
  clients: ZoneDriverClient[];
}

/** Server-enforced roster ceiling (`PUT` rejects anything above this). */
export const MAX_DRIVERS_PER_ZONE = 50;

/**
 * `400 DUPLICATE_DRIVER` — the payload listed the same driver id twice.
 * Carries the offending ids so the UI can point at the rows instead of just
 * failing.
 */
export class ZoneDriverDuplicateError extends Error {
  duplicates: number[];
  constructor(duplicates: number[]) {
    super(
      duplicates.length > 0
        ? `Mitra ${duplicates.join(', ')} appears more than once in the roster`
        : 'A mitra appears more than once in the roster'
    );
    this.name = 'ZoneDriverDuplicateError';
    this.duplicates = duplicates;
  }
}

type Envelope<T> = { status: string; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

export const zoneDriversAPI = {
  /** One zone's roster — for deep-links and post-save refresh. */
  listByZone: async (zoneId: string): Promise<ZoneDriver[]> => {
    try {
      const response = await Axios.get(`/v1/zones/${zoneId}/drivers`);
      const data = unwrap<{ zoneId: string; drivers: ZoneDriver[] }>(
        response.data
      );
      return data.drivers ?? [];
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Full replace — send the roster exactly as the UI orders it and the server
   * derives `priority = index + 1`. There is no add/remove/reorder endpoint;
   * every mutation is one PUT. An empty array clears the roster.
   */
  replace: async (
    zoneId: string,
    drivers: ZoneDriverInput[]
  ): Promise<ZoneDriver[]> => {
    try {
      const response = await Axios.put(`/v1/zones/${zoneId}/drivers`, {
        drivers,
      });
      const data = unwrap<{ zoneId: string; drivers: ZoneDriver[] }>(
        response.data
      );
      return data.drivers ?? [];
    } catch (error: any) {
      // The duplicate error arrives either at the envelope root or nested
      // under `data`, depending on which layer rejected it.
      const body = error?.response?.data;
      const payload =
        body && typeof body === 'object' && 'error' in body
          ? body
          : (body?.data ?? null);
      if (payload?.error === 'DUPLICATE_DRIVER') {
        throw new ZoneDriverDuplicateError(payload.duplicates ?? []);
      }
      throw new Error(getBackendErrorMessage(error));
    }
  },
};
